import { objectEntries } from 'lib/object/objectUtils'

type ScriptAttributes = Partial<HTMLScriptElement>

interface LoadScriptOptions {
  // Without this, a second call for a src whose tag is still downloading resolves straight away, before the script has run.
  // With it, every call for the same src waits on one load. Keyed by src only, so the first call's attributes win.
  shareInFlight?: boolean
  // Without this, a failed tag stays in the DOM and the next call counts it as loaded. With it, the next call downloads again.
  removeOnError?: boolean
}

type LaterLoader = {
  resolver: Promise<void>
  load: () => Promise<void>
}

const toLoad: Record<string, LaterLoader> = {}
const inFlight = new Map<string, Promise<void>>()

export default function loadScript(src: string, attributes: ScriptAttributes = {}, options: LoadScriptOptions = {}) {
  const sharedLoad = options.shareInFlight ? inFlight.get(src) : undefined
  if (sharedLoad) {
    return sharedLoad
  }

  const load = new Promise<void>(function(resolve, reject) {
    if (document == undefined) {
      return reject()
    }

    if (scriptAlreadyLoaded(src, attributes)) {
      setTimeout(resolve)
      return
    }

    const script = document.createElement('script')
    script.src = src
    for (const [key, value] of Object.entries(attributes)) {
      // Note: The attributes we're passing in are generally booleans,
      // but setAttribute insists they should be strings
      script.setAttribute(key, value as string)
    }
    script.onload = () => resolve()
    script.onerror = options.removeOnError ?
        (event) => {
          script.remove()
          reject(event)
        } :
      reject
    document.head.appendChild(script)
  })

  if (options.shareInFlight) {
    inFlight.set(src, load)
    // Cleared once settled: a loaded tag is then found by scriptAlreadyLoaded, and a failed load is not handed to the next call.
    const clear = () => inFlight.delete(src)
    load.then(clear, clear)
  }

  return load
}

export function scriptAlreadyLoaded(src: string, attributes: ScriptAttributes) {
  return Array.from(document.querySelectorAll('script'))
    .some(script => scriptMatches(script, src, attributes))
}

function scriptMatches(script: HTMLScriptElement, src: string, attributes: ScriptAttributes) {
  /* oxlint-disable @typescript-eslint/no-base-to-string */
  return script.src == src &&
    objectEntries(attributes).every(([key, value]) => typeof key === 'string' && script.getAttribute(key) == String(value ?? ''))
  /* oxlint-enable @typescript-eslint/no-base-to-string */
}

export function loadScriptOnReady(
  src: string,
  srcAttributes: Partial<HTMLScriptElement> = { async: true, defer: true },
) {
  if (document.readyState === 'complete') {
    return loadScript(src, srcAttributes)
  } else if (toLoad[src]) {
    return toLoad[src].resolver
  } else {
    const { promise, resolve } = Promise.withResolvers<void>()
    const loader = () => loadScript(src, srcAttributes).then(resolve)

    toLoad[src] = {
      resolver: promise,
      load: loader,
    }

    return promise
  }
}

(() => {
  if (typeof window !== 'undefined') {
    window.onload = () => {
      for (const [, laterLoader] of Object.entries(toLoad)) {
        laterLoader.load()
      }
    }
  }
})()
